import { useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShieldCheck, UserRound } from "lucide-react";
import { AiChat } from "@/components/ai/AiChat";
import { openAI } from "@/components/ai/stream";
import { useReferralClaim } from "@/components/engage/Referral";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useIsDesktop, useViewMode, inFrame, ViewModeSwitch } from "@/lib/viewMode";
import { OwnerPinDialog, openOwner } from "./OwnerPin";
import { InstallButton } from "./InstallButton";
import { MoreSheet } from "./MoreSheet";
import { InstallBanner } from "./InstallBanner";
import { Welcome } from "./Welcome";
import { IntroVideo } from "./IntroVideo";
import { SECTIONS, TABS, TITLES } from "./nav";

export const Logo = ({ small }) => (
  <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid={small ? "brand-mobile" : "brand"}>
    <img src="/icon-192.png" alt="" className={`${small ? "w-8 h-8" : "w-9 h-9"} rounded-[10px]`} />
    <span className={`font-head font-extrabold uppercase tracking-wide ${small ? "text-xl" : "text-2xl"}`}>BTC <span className="text-[var(--cyan)]">Odds</span> Watch</span>
  </Link>
);

const SideItem = ({ to, label, Icon, id }) => to === "ai" ? (
  <button onClick={() => openAI({})} className="w-full flex gap-3 px-3 py-2.5 rounded-xl text-sm items-center font-semibold text-slate-400 hover:text-slate-100 hover:bg-white/[0.03]" data-testid={`side-nav-${id}`}><Icon className="w-4 h-4 text-[var(--violet)]" />{label}</button>
) : (
  <NavLink to={to} end={to === "/"} data-testid={`side-nav-${id}`}
    className={({ isActive }) => `flex gap-3 px-3 py-2.5 rounded-xl text-sm items-center font-semibold transition-colors ${isActive ? "text-[var(--cyan)] bg-[var(--cyan)]/10" : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.03]"}`}>
    <Icon className="w-4 h-4" />{label}</NavLink>
);

const Sidebar = () => {
  const { owner } = useAccess();
  return (
    <aside className="fixed inset-y-0 left-0 w-64 flex flex-col border-r border-[var(--line)] bg-[var(--bg)]/95 p-4 gap-4 z-40 overflow-y-auto no-scrollbar" data-testid="sidebar">
      <div className="px-2 py-2"><Logo /></div>
      {SECTIONS.map(([title, items]) => (
        <div key={title} className="space-y-0.5"><div className="px-3 pb-1 text-[10px] uppercase tracking-[.18em] text-slate-600 font-bold">{title}</div>
          {items.map(([to, l, I, id]) => <SideItem key={id} to={to} label={l} Icon={I} id={id} />)}</div>))}
      <div className="mt-auto space-y-3">
        <button onClick={openOwner} className={`w-full flex gap-3 px-3 py-2.5 rounded-xl text-sm items-center font-bold border ${owner ? "border-[var(--gold)]/50 text-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--line)] text-slate-400 hover:text-[var(--gold)]"}`} data-testid="side-nav-owner">
          <ShieldCheck className="w-4 h-4" />{owner ? "Owner console" : "Owner login"}</button>
        <ViewModeSwitch />
        <InstallButton />
      </div>
    </aside>
  );
};

const TopBar = ({ onMore }) => {
  const { isPro, tier } = useAuth();
  const { owner } = useAccess();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[var(--bg)]/85 border-b border-[var(--line)] px-4 pt-[env(safe-area-inset-top)]" data-testid="top-bar">
      <div className="h-14 flex items-center justify-between gap-2">
        <Logo small />
        <div className="flex items-center gap-2">
          {isPro ? <span className="pill-pro" data-testid="pro-badge">{tier.toUpperCase()}</span> :
            <Link to="/pro" className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1.5 rounded-lg bg-[var(--gold)]/15 text-[var(--gold)]" data-testid="header-pro-link">Try Pro</Link>}
          <button onClick={openOwner} aria-label="Owner console" data-testid="header-owner-btn"
            className={`h-9 w-9 rounded-xl border flex items-center justify-center ${owner ? "border-[var(--gold)]/60 text-[var(--gold)]" : "border-[var(--line)] text-slate-400"}`}><ShieldCheck className="w-4 h-4" /></button>
          <Link to="/account" className="h-9 w-9 rounded-xl border border-[var(--line)] flex items-center justify-center" data-testid="header-account-link" aria-label="Account"><UserRound className="w-4 h-4" /></Link>
        </div>
      </div>
    </header>
  );
};

const TabBar = ({ onMore }) => (
  <nav className="fixed bottom-0 inset-x-0 z-40 border-t border-[var(--line)] bg-[#0A0E17]/95 backdrop-blur-xl flex pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
    {TABS.map(([to, l, I, t]) => (
      <NavLink key={to} to={to} end={to === "/"} data-testid={t}
        className={({ isActive }) => `relative flex-1 flex flex-col items-center gap-1 pt-2.5 pb-2 text-[10px] font-bold uppercase tracking-wide transition-colors ${isActive ? "text-[var(--cyan)]" : "text-slate-500"}`}>
        {({ isActive }) => <>{isActive && <motion.span layoutId="tab-bar" className="tab-active-bar" transition={{ type: "spring", stiffness: 500, damping: 35 }} />}
          <motion.span animate={{ scale: isActive ? 1.12 : 1, y: isActive ? -1 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}><I className="w-5 h-5" /></motion.span>{l}</>}
      </NavLink>))}
    <button onClick={onMore} className="flex-1 flex flex-col items-center gap-1 pt-2.5 pb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500" data-testid="nav-more"><Menu className="w-5 h-5" />More</button>
  </nav>
);

const PhonePreview = () => (
  <div className="min-h-screen flex items-center justify-center gap-10 p-6" data-testid="phone-preview">
    <iframe title="BTC Odds Watch mobile" src={window.location.href} className="phone-frame" data-testid="phone-frame" />
    <div className="hidden md:block w-64 space-y-3"><Logo /><p className="text-sm text-slate-400">You're previewing the mobile layout on a computer.</p><ViewModeSwitch /></div>
  </div>
);

export const AppShell = ({ children }) => {
  const desktop = useIsDesktop();
  const mode = useViewMode();
  const loc = useLocation();
  const { pathname } = loc;
  useReferralClaim();
  useEffect(() => {
    const base = Object.keys(TITLES).find((k) => k !== "/" && pathname.startsWith(k)) || "/";
    document.title = `${TITLES[base] || TITLES["/"]} | BTC Odds Watch`;
  }, [pathname]);
  const more = () => window.dispatchEvent(new Event("more-open"));
  const { profile } = useAuth();
  const open = ["/pro", "/learn"].some((p) => pathname.startsWith(p));
  if (profile === undefined) return <div className="min-h-screen" />;
  if (!profile && !open) return <><Welcome /><IntroVideo /></>;
  if (mode === "mobile" && !inFrame() && window.innerWidth >= 700) return <PhonePreview />;
  return (
    <div className={`min-h-screen ${desktop ? "pl-64" : ""}`} data-testid="app-shell">
      {desktop ? <Sidebar /> : <TopBar onMore={more} />}
      <AnimatePresence mode="wait">
        <motion.main key={pathname} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: "easeOut" }} className={`max-w-6xl mx-auto px-4 sm:px-6 pt-5 ${desktop ? "pb-12" : "pb-32"}`}>
          {children}
        </motion.main>
      </AnimatePresence>
      {!desktop && <TabBar onMore={more} />}
      <MoreSheet />
      <InstallBanner />
      <IntroVideo />
      <OwnerPinDialog />
      <AiChat />
    </div>
  );
};
